import { ImagePicker } from "features/editor/sidebar/image/ImagePicker";
import { useAppSelector } from "lib/redux/hooks";
import { authSelectors } from "features/auth/authSlice";
import { fetchImageCollection, ImageGroup } from "features/editor/sidebar/image/imageCollections";

import { FormGroup } from "components/UIComponents/FormGroup";
import { FormInput } from "components/UIComponents/FormInput";
import { FormSelect } from "components/UIComponents/FormSelect";
import { SliderToggle } from "components/UIComponents/SliderToggle";

import { ReactElement, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AperitifPresetValues } from "lib/api/aperitifMenu";
import { Tab } from "@headlessui/react";
import { DRINK_SIZES, DrinkSize } from "features/editor/MenuDocumentModel";
import { identity } from "ramda";

const DESCRIPTION_MAX_LENGTH = 200;

interface AperitifPresetFormProps {
    values: AperitifPresetValues;
    onChange: <T extends keyof AperitifPresetValues>(field: T, value: AperitifPresetValues[T]) => void;
}

export function AperitifPresetForm({ values, onChange }: AperitifPresetFormProps): ReactElement {
    return (
        <div className="flex flex-col gap-6">
            <Tab.Group>
                <Tab.List className="flex gap-10">
                    <Tab as="div">
                        {({selected}) => (
                            <button
                                className={`font-medium
                                            ${selected ? 'text-text border-b-2 border-b-text' : 'text-gray-1'}`}>
                                <FormattedMessage id="editor.wine.preferences"/>
                            </button>
                        )}
                    </Tab>
                    <Tab as="div">
                        {({selected}) => (
                            <button
                                className={`font-medium
                                            ${selected ? 'text-text border-b-2 border-b-text' : 'text-gray-1'}`}>
                                <FormattedMessage id="editor.wine.price"/>
                            </button>
                        )}
                    </Tab>
                </Tab.List>
                <Tab.Panels>
                    <Tab.Panel>
                        <PropertiesPanel values={values} onChange={onChange} />
                    </Tab.Panel>
                    <Tab.Panel>
                        <PricePanel values={values} onChange={onChange} />
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </div>
    );
}

interface PropertiesPanelProps {
    values: AperitifPresetValues;
    onChange: <T extends keyof AperitifPresetValues>(field: T, value: AperitifPresetValues[T]) => void;
}

function PropertiesPanel({ values, onChange }: PropertiesPanelProps): ReactElement {
    const intl = useIntl();
    const userId = useAppSelector(authSelectors.customer);
    const fetchWineImageCollection = useCallback((): Promise<ImageGroup[]> =>
        userId ?
            fetchImageCollection({ userId, collection: "aperitif" })
            : Promise.resolve([]),
        [userId]
    );

    return (
        <>
            <FormGroup groupTitle={<FormattedMessage id="aperitif.form.name.label" />}>
                <FormInput
                    value={values.name}
                    placeholder={intl.formatMessage({ id: "aperitif.form.name.placeholder" })}
                    onChange={e => onChange("name", e.target.value)}
                />
            </FormGroup>

            <FormGroup
                groupTitle={<FormattedMessage id="aperitif.form.description.label" />}
                chars={values.description.length}
                maxChars={DESCRIPTION_MAX_LENGTH}
            >
                <textarea
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    rows={4}
                    placeholder={intl.formatMessage({ id: "aperitif.form.description.placeholder" })}
                    className="py-3 pl-4 pr-2 w-full text-sm rounded border border-gray-1 outline-0 focus:border-focus resize-none"
                    value={values.description}
                    onChange={e => onChange("description", e.target.value)}
                />
            </FormGroup>

            <FormGroup groupTitle={<FormattedMessage id="aperitif.form.image.label" />}>
                <ImagePicker
                    images={fetchWineImageCollection}
                    currentImage={values.imageUrl ?? null}
                    onPick={url => onChange("imageUrl", url ?? undefined)}
                    allowClear={true}
                    upload={{
                        userId: userId!,
                        collection: 'aperitif',
                        allowResize: true,
                        resizeAspect: 40 / 114
                    }}
                />
            </FormGroup>
        </>
    );
}

interface PricePanelProps {
    values: AperitifPresetValues;
    onChange: <T extends keyof AperitifPresetValues>(field: T, value: AperitifPresetValues[T]) => void;
}

function PricePanel({ values, onChange }: PricePanelProps): ReactElement {
    const intl = useIntl();
    const isStandardSize = (DRINK_SIZES as readonly string[]).includes(values.size);
    const [useStandardSize, setUseStandardSize] = useState<boolean>(isStandardSize);

    return (
        <>
            <FormGroup groupTitle={<FormattedMessage id="editor.wine.price.salePrice" />}>
                <div className="grid grid-cols-2 text-xs text-gray-1 mb-1 px-1">
                    <span><FormattedMessage id="aperitif.form.quantity.label" /></span>
                    <span><FormattedMessage id="editor.wine.price" /></span>
                </div>
                <div className="grid grid-cols-2 items-center gap-3">
                    <div className="flex-auto">
                        {useStandardSize ? (
                            <FormSelect
                                options={DRINK_SIZES}
                                selected={(values.size || DRINK_SIZES[0]) as DrinkSize}
                                onChange={s => onChange("size", s)}
                                getLabel={identity}
                            />
                        ) : (
                            <FormInput
                                value={values.size}
                                placeholder={intl.formatMessage({ id: "aperitif.form.quantity.placeholder" })}
                                onChange={e => onChange("size", e.target.value)}
                            />
                        )}
                    </div>
                    <div className="flex-auto">
                        <FormInput
                            value={values.price}
                            type="number"
                            placeholder={intl.formatMessage({ id: "aperitif.form.price.placeholder" })}
                            onChange={e => onChange("price", e.target.value)}
                        />
                    </div>
                </div>
            </FormGroup>
            <FormGroup groupTitle={<FormattedMessage id="aperitif.form.customSize.label" />}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-5">
                        <SliderToggle
                            enabled={!useStandardSize}
                            setEnabled={enabled => setUseStandardSize(!enabled)}
                        />
                    </div>
                </div>
            </FormGroup>
        </>
    );
}
